import api from '@api';
import Loader from '@components/common/Loader';
import InsightVisualization from '@components/layout/main/InsightVisualization';
import config from '@config';
import { Log, Metric } from '@enums';
import { useElementBreakpoints, useWordStream } from '@hooks';
import BackIcon from '@icons/back.svg?react';
import CloseIcon from '@icons/close.svg?react';
import { useChatStore } from '@store/chat';
import { useShellStore } from '@store/shell';
import { log } from '@utils';
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import type { InsightChart } from 'types/schema';
import Styled from './RightPanel.styled';

function RightPanel() {
  const sourceList = useChatStore((store) => store.insightList);
  const chatId = useChatStore((store) => store.chat.id);
  const getRelatedPromptMessage = useChatStore(
    (store) => store.getRelatedPromptMessage
  );
  const shellStore = useShellStore();
  const shouldShowCompactChart = useElementBreakpoints(
    document.body,
    checkBreakpoints
  );
  const [chartDataList, setChartDataList] = useState<InsightChart[]>([]);
  // [Mobile] Track the index of the visible insight visualization
  const [visibleInsightIndex, setVisibleInsightIndex] = useState<number>(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const [streamedInsightText] = useWordStream(
    chartDataList[visibleInsightIndex]?.insight.text || '',
    0 // or 10 for word streaming
  );

  useEffect(() => {
    // Scroll to top when insights list is updated
    wrapperRef.current?.scrollTo({ top: 0, behavior: 'instant' });

    // Reset the list
    startTransition(() => {
      setChartDataList([]);
    });

    sourceList.forEach((source, index) => {
      api.sources
        .getChartData(source.id)
        .then((chartData) => {
          startTransition(() => {
            setChartDataList((list) => {
              const next = [...list];
              next[index] = chartData;
              return next;
            });
          });
          log('Load chart data for source.', {
            data: chartData,
            id: source.id
          });
        })
        .catch(() => {
          shellStore.setIsRightPanelOpen(false);
          shellStore.setToast({
            message: 'Failed to load chart data for insight.',
            isError: true
          });
        });
    });
  }, [sourceList, shellStore.setIsRightPanelOpen, shellStore.setToast]);

  useEffect(() => {
    const handleScroll = (event: Event) => {
      const element = event.target as HTMLElement;

      if (element.scrollTop > 0) {
        headerRef.current?.classList.add('shadow');
      } else {
        headerRef.current?.classList.remove('shadow');
      }
    };

    wrapperRef.current?.addEventListener('scroll', handleScroll);

    return () => {
      wrapperRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleClose = () => {
    shellStore.setIsRightPanelOpen(false);
  };

  const handleChangeMetric = useCallback(
    (metric: Metric) => {
      // Log the event of changing the metric
      api.analytics.selectedChartMetric({
        ['chat_id']: chatId,
        ['chart_metric']:
          metric === Metric.Universe ? 'universe' : metric.valueOf(),
        ['query']: getRelatedPromptMessage().prompt
      });
    },
    [chatId, getRelatedPromptMessage]
  );

  // This has an impact only on mobile devices
  const setupInsightIntersectionObserver = useCallback(
    (element: HTMLDivElement) => {
      if (element) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const index = entry.target.closest<HTMLElement>(
                  '.insight-visualization'
                ).dataset.index;

                if (import.meta.env.DEV) {
                  log(
                    `Insight visualization is visible at index ${index}`,
                    Log.Debug
                  );
                }

                setVisibleInsightIndex(Number(index));
              }
            });
          },
          {
            root: wrapperRef.current,
            rootMargin: '-180px 0px 0px',
            threshold: 0.6
          }
        );

        const insightVisualizationTable = element.querySelector(
          '.insight-visualization-table'
        );

        if (insightVisualizationTable) {
          observer.observe(insightVisualizationTable);
        }

        return () => {
          observer.disconnect();
        };
      }
    },
    []
  );

  const charts = useMemo(
    () =>
      sourceList.map((source, index) =>
        chartDataList[index] ? (
          <InsightVisualization
            data={chartDataList[index]}
            id={source.id}
            index={index}
            ref={
              // [Mobile] Use intersection observer if multiple charts
              chartDataList.length > 1 ? setupInsightIntersectionObserver : null
            }
            hasCompactChart={shouldShowCompactChart}
            key={`insight-visualization-${source.id}`}
            prompt={getRelatedPromptMessage().prompt}
            onChangeMetric={handleChangeMetric}
          />
        ) : (
          <Styled.Loader key={`loader-${source.id}`}>
            <Loader />
          </Styled.Loader>
        )
      ),
    [
      sourceList,
      chartDataList,
      shouldShowCompactChart,
      handleChangeMetric,
      getRelatedPromptMessage,
      setupInsightIntersectionObserver
    ]
  );

  return (
    <Styled.Wrapper
      className="overlay-scrollbar"
      isOpen={shellStore.isRightPanelOpen}
      data-testid="right-panel"
      ref={wrapperRef}>
      <Styled.Header ref={headerRef}>
        <Styled.Title>Sources used ({sourceList.length})</Styled.Title>
        <Styled.CloseButton onClick={handleClose}>
          <BackIcon className="back icon" />
          <CloseIcon className="close icon" />
        </Styled.CloseButton>
        <Styled.Subtitle>{streamedInsightText}</Styled.Subtitle>
      </Styled.Header>
      <Styled.Content>{charts}</Styled.Content>
    </Styled.Wrapper>
  );
}

const checkBreakpoints = (inlineSize: number) => [
  inlineSize < config.chart.compact.breakpoint,
  inlineSize > config.breakpoints.medium &&
    inlineSize <= config.breakpoints.xLarge
];

export default RightPanel;
